import React, { useRef } from "react";
import {
  Box,
  ButtonBase,
  CircularProgress,
  GlobalStyles,
  InputAdornment,
  Slider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckIcon from "@mui/icons-material/Check";
import {
  Autocomplete,
  CircleF,
  GoogleMap,
  MarkerF,
  useJsApiLoader,
} from "@react-google-maps/api";
import {
  CIRCLE_OPTIONS,
  GEOFENCE_MAX_RADIUS,
  GEOFENCE_MIN_RADIUS,
  GEOFENCE_MODES,
  GEOFENCE_RADIUS_STEP,
  GF,
  MAPS_LIBRARIES,
  MAPS_LOADER_ID,
  fitMapToCircle,
  formatRadius,
} from "./geofenceApi";

// India — same default center as the address map.
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };

export const useGeofenceMapsLoader = () =>
  useJsApiLoader({
    id: MAPS_LOADER_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY,
    libraries: MAPS_LIBRARIES,
  });

/** Team page status chip: dashed "+ Add Geofence" vs tinted "Geofencing On". */
export const GeofenceChip = ({ active, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "5px",
      px: "10px",
      py: "5px",
      borderRadius: "8px",
      border: active ? "1px solid transparent" : `1px dashed ${GF.primary}`,
      bgcolor: active ? GF.primaryTint : "#fff",
      color: GF.primary,
      fontSize: 13,
      fontWeight: 600,
      lineHeight: 1.3,
      whiteSpace: "nowrap",
    }}
  >
    <FmdGoodOutlinedIcon sx={{ fontSize: 15 }} />
    {active ? "Geofencing On" : "+ Add Geofence"}
  </ButtonBase>
);

/**
 * Map with place search, click-to-drop-pin and the radius circle.
 * `place` is { name, address, lat, lng } or null.
 */
export const GeofenceMap = ({ isLoaded, place, radius, onPlaceChange, height = 520 }) => {
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const placeRef = useRef(place);
  placeRef.current = place;
  const radiusRef = useRef(radius);
  radiusRef.current = radius;

  const handleMapLoad = (map) => {
    mapRef.current = map;
    if (placeRef.current) {
      fitMapToCircle(map, placeRef.current, radiusRef.current);
      return;
    }
    map.setCenter(DEFAULT_CENTER);
    map.setZoom(5);
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (placeRef.current) return; // leader already picked a place
          map.panTo({ lat: position.coords.latitude, lng: position.coords.longitude });
          map.setZoom(15);
        },
        () => {}
      );
    }
  };

  const handlePlaceChanged = () => {
    const result = autocompleteRef.current?.getPlace();
    if (!result?.geometry?.location) return;
    const next = {
      name: result.name || result.formatted_address || "",
      address: result.formatted_address || "",
      lat: result.geometry.location.lat(),
      lng: result.geometry.location.lng(),
    };
    onPlaceChange(next);
    fitMapToCircle(mapRef.current, next, radiusRef.current);
  };

  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    const next = { name: "", address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`, lat, lng };
    onPlaceChange(next);

    // Best-effort human-readable address for the clicked point.
    new window.google.maps.Geocoder().geocode({ location: { lat, lng } }, (results, status) => {
      if (status !== "OK" || !results?.[0]) return;
      const current = placeRef.current;
      if (!current || current.lat !== lat || current.lng !== lng) return;
      const components = results[0].address_components || [];
      const pick = (type) => components.find((c) => c.types.includes(type))?.long_name;
      const shortName = [pick("sublocality_level_1") || pick("sublocality") || pick("neighborhood"), pick("locality")]
        .filter(Boolean)
        .join(", ");
      onPlaceChange({ ...current, name: shortName, address: results[0].formatted_address });
    });
  };

  // Keep the circle in view when the radius changes from outside (slider),
  // once the leader stops dragging rather than on every step.
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (placeRef.current) fitMapToCircle(mapRef.current, placeRef.current, radius);
    }, 300);
    return () => clearTimeout(timer);
  }, [radius]);

  if (!isLoaded) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height }}>
        <CircularProgress />
      </Box>
    );
  }

  const center = place ? { lat: place.lat, lng: place.lng } : null;
  return (
    <Box sx={{ position: "relative" }}>
      {/* Places suggestions render at z-index 1000, behind MUI dialogs (1300). */}
      <GlobalStyles styles={{ ".pac-container": { zIndex: 1500 } }} />
      <Box sx={{ position: "absolute", top: 16, left: 16, right: 16, zIndex: 1 }}>
        <Autocomplete
          onLoad={(ref) => (autocompleteRef.current = ref)}
          onPlaceChanged={handlePlaceChanged}
          fields={["geometry", "name", "formatted_address"]}
        >
          <TextField
            fullWidth
            placeholder="Enter Location"
            sx={{
              bgcolor: GF.field,
              borderRadius: "10px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              "& fieldset": { border: "none" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: GF.muted }} />
                </InputAdornment>
              ),
            }}
          />
        </Autocomplete>
      </Box>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height }}
        onLoad={handleMapLoad}
        onClick={handleMapClick}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          clickableIcons: false,
        }}
      >
        {center && (
          <>
            <CircleF center={center} radius={radius} options={CIRCLE_OPTIONS} />
            <MarkerF position={center} />
          </>
        )}
      </GoogleMap>
    </Box>
  );
};

/** Radius value + slider + "100 m / 1 km" captions. */
export const RadiusControl = ({ value, onChange, disabled, large }) => (
  <Box>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
      {!large && (
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: GF.foreground }}>Radius</Typography>
      )}
      <Typography
        sx={{ fontSize: large ? 32 : 17, fontWeight: 700, color: disabled ? GF.muted : GF.primary }}
      >
        {formatRadius(value)}
      </Typography>
    </Box>
    <Slider
      value={value}
      min={GEOFENCE_MIN_RADIUS}
      max={GEOFENCE_MAX_RADIUS}
      step={GEOFENCE_RADIUS_STEP}
      disabled={disabled}
      onChange={(_, v) => onChange(v)}
      aria-label="Geofence radius in meters"
      sx={{
        color: GF.primary,
        mt: 1,
        "& .MuiSlider-thumb": { bgcolor: "#fff", border: `3px solid ${GF.primary}`, width: 22, height: 22 },
        "& .MuiSlider-rail": { bgcolor: GF.border, opacity: 1 },
      }}
    />
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <Typography sx={{ fontSize: 13, color: GF.muted }}>100 m</Typography>
      <Typography sx={{ fontSize: 13, color: GF.muted }}>1 km</Typography>
    </Box>
  </Box>
);

/** "Time In Only" / "Time In & Time Out" segmented control. */
export const ModeToggle = ({ value, onChange }) => (
  <ToggleButtonGroup
    exclusive
    fullWidth
    value={value}
    onChange={(_, v) => v && onChange(v)}
    sx={{
      bgcolor: GF.field,
      p: "5px",
      gap: "5px",
      borderRadius: "10px",
      "& .MuiToggleButton-root": {
        border: "none !important",
        borderRadius: "8px !important",
        textTransform: "none",
        fontWeight: 600,
        fontSize: 13,
        color: GF.muted,
        py: 1,
      },
      "& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover": {
        bgcolor: GF.primary,
        color: "#fff",
      },
    }}
  >
    {GEOFENCE_MODES.map((m) => (
      <ToggleButton key={m.value} value={m.value}>
        {m.label}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

const CheckBox = ({ checked }) => (
  <Box
    sx={{
      width: 24,
      height: 24,
      borderRadius: "7px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      bgcolor: checked ? GF.primary : "transparent",
      border: checked ? "none" : `1.5px solid ${GF.border}`,
    }}
  >
    {checked && <CheckIcon sx={{ fontSize: 16, color: "#fff" }} />}
  </Box>
);

const memberDepartment = (member) => {
  const dept = member.department_data?.[0] || (typeof member.department === "object" ? member.department : null);
  return dept ? { name: dept.department_name || dept.name || "", colour: dept.colour } : null;
};

/** "Select All Members" card; `selectedIds` is a Set of team_members _id. */
export const MemberChecklist = ({ members, selectedIds, onChange, maxHeight }) => {
  const total = members.length;
  const selected = members.filter((m) => selectedIds.has(m._id)).length;
  const allSelected = total > 0 && selected === total;

  const toggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  return (
    <Box sx={{ border: `1px solid ${GF.border}`, borderRadius: "14px", px: "18px", py: "4px", bgcolor: "#fff" }}>
      <ButtonBase
        onClick={() => onChange(allSelected ? new Set() : new Set(members.map((m) => m._id)))}
        disabled={total === 0}
        sx={{ width: "100%", display: "flex", gap: 1.5, py: "14px", justifyContent: "flex-start" }}
        role="checkbox"
        aria-checked={allSelected}
      >
        <Typography sx={{ flex: 1, textAlign: "left", fontSize: 15, fontWeight: 600, color: GF.foreground }}>
          Select All Members
        </Typography>
        <Typography sx={{ fontSize: 13, color: GF.muted }}>
          {selected} of {total} selected
        </Typography>
        <CheckBox checked={allSelected} />
      </ButtonBase>
      <Box sx={{ height: "1px", bgcolor: GF.border }} />
      {total === 0 ? (
        <Typography sx={{ py: 2, fontSize: 14, color: GF.muted }}>No members in this team yet.</Typography>
      ) : (
        <Box sx={{ maxHeight, overflowY: maxHeight ? "auto" : "visible" }}>
          {members.map((member) => {
            const dept = memberDepartment(member);
            const checked = selectedIds.has(member._id);
            return (
              <ButtonBase
                key={member._id}
                onClick={() => toggle(member._id)}
                role="checkbox"
                aria-checked={checked}
                sx={{ width: "100%", display: "flex", gap: 1.5, py: "14px", justifyContent: "flex-start" }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: dept?.colour || GF.primary,
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  {(member.user_name || "?").trim().charAt(0).toUpperCase()}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <Typography noWrap sx={{ fontSize: 15, fontWeight: 600, color: GF.foreground }}>
                    {member.user_name}
                  </Typography>
                  {dept?.name && (
                    <Typography noWrap sx={{ fontSize: 12, color: GF.muted }}>
                      {dept.name}
                    </Typography>
                  )}
                </Box>
                <CheckBox checked={checked} />
              </ButtonBase>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

/** Tappable "label / value" card on the geofence view page. */
export const DetailRow = ({ icon: Icon, label, value, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      width: "100%",
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      p: "14px",
      border: `1px solid ${GF.border}`,
      borderRadius: "14px",
      bgcolor: "#fff",
      textAlign: "left",
      "&:hover": { bgcolor: "#FAFAFB" },
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "8px",
        bgcolor: GF.field,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: 20, color: GF.primary }} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: 14, color: GF.muted }}>{label}</Typography>
      <Typography noWrap sx={{ fontSize: 17, fontWeight: 600, color: GF.foreground }}>
        {value}
      </Typography>
    </Box>
    <ChevronRightIcon sx={{ color: GF.muted }} />
  </ButtonBase>
);
